import React, { useEffect } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../utils/inputs/Input/Input';
import { Form, StyledButton } from './DomesticWireForm.styles';
import { notifyError } from '../../../utils/notify';
import { handleWithdrawalMethods } from '../../../store/slices/user/asyncThunks';
import { WITHDRAW } from '../../App/routes';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUserActionsPending } from '../../../store/slices/user/slice';
import { WITHDRAWAL_METHOD_PROFILE_TYPE, WITHDRAWAL_METHOD_TYPE } from '../../../utils/constants';
import UploadDocumentPhotoInput from '../../utils/inputs/UploadDocumentPhotoInput/UploadDocumentPhotoInput';
import { ReactComponent as IdCardFront } from '../../../assets/icons/id-front.svg';
import { ReactComponent as IdCardBack } from '../../../assets/icons/id-back.svg';
import ProfileTypeSwitch from '../ProfileTypeSwitch/ProfileTypeSwitch';
import { useConfirmationModal } from '../../modals/ConfirmationModal/ConfirmationModal';

export const domesticWireFormSchema = yup
  .object({
    accountNumber: yup.string().trim().required('Account Number is required'),
    routingNumber: yup.string().trim().required('Routing Number is required'),
    beneficiaryName: yup.string().trim().required('Beneficiary Name is required'),
    beneficiaryAddress: yup.string().trim().required('Beneficiary Address is required'),
    ssn: yup
      .string()
      .trim()
      .when('profileType', {
        is: WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL,
        then: schema => schema.required('Social Security Number is required'),
      }),
    verificationDocumentFront: yup.mixed().required('ID Card is required'),
    verificationDocumentBack: yup.mixed().required('ID Card is required'),
  })
  .required();

const DomesticWireForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleWithdrawalMethods: handleWithdrawalMethodsPending } = useSelector(selectUserActionsPending);
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm({
    defaultValues: {
      accountNumber: '',
      routingNumber: '',
      beneficiaryName: '',
      beneficiaryAddress: '',
      ssn: '',
      verificationDocumentFront: null,
      verificationDocumentBack: null,
      profileType: WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL,
    },
    delayError: 300,
    resolver: yupResolver(domesticWireFormSchema),
  });

  const profileTypeWatch = watch('profileType');

  useEffect(() => {
    setConfirmationModalLoader(handleWithdrawalMethodsPending);
    // eslint-disable-next-line
  }, [handleWithdrawalMethodsPending]);

  const onSubmit = data => {
    openModal(
      'Confirm Banking Details',
      `Please make sure everything’s correct. You can change it later by deleting and adding new details.`,
      'Confirm & Save',
      () => {
        dispatch(handleWithdrawalMethods({ ...data, withdrawalMethodType: WITHDRAWAL_METHOD_TYPE.DOMESTIC_WIRE }))
          .unwrap()
          .then(() => {
            closeModal();
            navigate(`/${WITHDRAW}`);
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Go Back',
      () => {
        closeModal();
      },
    );
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="profileType"
        control={control}
        render={({ field }) => {
          return <ProfileTypeSwitch value={field.value} onChange={field.onChange} />;
        }}
      />
      <Input
        label={profileTypeWatch === WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL ? 'Beneficiary Name' : 'Business Name'}
        placeholder={
          profileTypeWatch === WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL
            ? 'Enter Beneficiary Name'
            : 'Enter Business Name'
        }
        {...register('beneficiaryName')}
        error={errors.beneficiaryName?.message}
      />
      <Input
        label={
          profileTypeWatch === WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL ? 'Beneficiary Address' : 'Business Address'
        }
        placeholder={
          profileTypeWatch === WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL
            ? 'Enter Beneficiary Address'
            : 'Enter Business Address'
        }
        {...register('beneficiaryAddress')}
        error={errors.beneficiaryAddress?.message}
      />
      {profileTypeWatch === WITHDRAWAL_METHOD_PROFILE_TYPE.PERSONAL && (
        <Input
          label="Social Security Number"
          placeholder="Enter Social Security Number"
          {...register('ssn')}
          error={errors.ssn?.message}
        />
      )}
      <Input
        label="Account Number"
        placeholder="Enter Account Number"
        {...register('accountNumber')}
        error={errors.accountNumber?.message}
      />
      <Input
        label="Routing Number"
        placeholder="Enter Routing Number"
        {...register('routingNumber')}
        error={errors.routingNumber?.message}
      />
      <Controller
        name="verificationDocumentFront"
        control={control}
        render={({ field }) => {
          return (
            <UploadDocumentPhotoInput
              value={field.value}
              onChange={field.onChange}
              error={errors.verificationDocumentFront?.message}
              title="ID Card - Front"
              icon={<IdCardFront />}
            />
          );
        }}
      />
      <Controller
        name="verificationDocumentBack"
        control={control}
        render={({ field }) => {
          return (
            <UploadDocumentPhotoInput
              value={field.value}
              onChange={field.onChange}
              error={errors.verificationDocumentBack?.message}
              title="ID Card - Back"
              icon={<IdCardBack />}
            />
          );
        }}
      />
      <StyledButton title="Save" type="submit" isLoading={handleWithdrawalMethodsPending} />
    </Form>
  );
};

export default DomesticWireForm;
